<section class="platform-page">
  <header class="platform-page__header">
    <div class="platform-page__heading">
      <p class="platform-page__eyebrow">Dashboard</p>
      <h2>Platform Overview</h2>
      <p>Track channel growth, conversation volume, and user activity across the platform.</p>
    </div>

    <button mat-stroked-button type="button" (click)="loadDashboard()">Refresh</button>
  </header>

  @if (loading()) {
    <section class="dashboard-state-card">
      <strong>Loading dashboard metrics...</strong>
      <p>We are pulling the latest platform activity now.</p>
    </section>
  } @else if (errorMessage()) {
    <section class="dashboard-state-card">
      <strong>Unable to load dashboard</strong>
      <p>{{ errorMessage() }}</p>
    </section>
  } @else if (dashboard(); as data) {
    <div class="summary-grid">
      <article class="summary-card">
        <div class="summary-card__header">
          <span class="summary-card__label">Total chat channels</span>
          <strong>{{ formatCount(data.summary.total_channels) }}</strong>
        </div>
        <p>All channels created across organizations.</p>
      </article>

      <article class="summary-card">
        <div class="summary-card__header">
          <span class="summary-card__label">Messages tracked</span>
          <strong>{{ formatCount(data.summary.total_messages) }}</strong>
        </div>
        <p>Non-system messages only.</p>
      </article>

      <article class="summary-card">
        <div class="summary-card__header">
          <span class="summary-card__label">Active users today</span>
          <strong>{{ formatCount(data.summary.active_users_today) }}</strong>
        </div>
        <p>Users who sent at least one non-system message today.</p>
      </article>

      <article class="summary-card">
        <div class="summary-card__header">
          <span class="summary-card__label">Active users in 14 days</span>
          <strong>{{ formatCount(data.summary.active_users_last_14d) }}</strong>
        </div>
        <p>Distinct active senders across the last two weeks.</p>
      </article>
    </div>

    <section class="dashboard-panel">
      <header class="dashboard-panel__header">
        <div>
          <h3>User usage graph</h3>
          <p>Daily active users based on non-system chat activity over the last 14 days.</p>
        </div>
      </header>

      <div class="usage-chart" aria-label="Daily active users over the last 14 days">
        @for (point of data.usage; track point.date) {
          <div class="usage-chart__item" [attr.title]="point.label + ': ' + formatCount(point.active_users) + ' active users, ' + formatCount(point.messages) + ' messages'">
            <div class="usage-chart__bar-track">
              <div class="usage-chart__bar" [style.height]="usageBarHeight(point.active_users)"></div>
            </div>
            <strong>{{ formatCount(point.active_users) }}</strong>
            <span>{{ point.label }}</span>
          </div>
        }
      </div>
    </section>

    <section class="dashboard-panel">
      <header class="dashboard-panel__header">
        <div>
          <h3>Channel message counts</h3>
          <p>Message totals per channel with system-generated messages excluded.</p>
        </div>
      </header>

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Channel</th>
              <th>Organization</th>
              <th>Participants</th>
              <th>Messages</th>
              <th>Last activity</th>
            </tr>
          </thead>
          <tbody>
            @for (channel of data.channels; track channel.channel_id) {
              <tr>
                <td>
                  <div class="channel-meta">
                    <strong>{{ formatChannelName(channel) }}</strong>
                    <span>{{ channel.channel_id }}</span>
                  </div>
                </td>
                <td>{{ channel.organization_name || channel.organization_id }}</td>
                <td>{{ formatCount(channel.participant_count) }}</td>
                <td>{{ formatCount(channel.message_count) }}</td>
                <td>{{ formatDate(channel.last_activity_at) }}</td>
              </tr>
            }

            @if (!data.channels.length) {
              <tr>
                <td class="table-empty" colspan="5">No channel data available yet.</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </section>
  }
</section>
